// MACHINERY V2 (Stroje) — the fleet SCROLL AREA: notices, strip, view branch
// (split out of FleetPage.tsx at M2.13).
//
// >>> WHY THIS FILE EXISTS. <<< FleetPage was at 290 of the project's 300 lines
// and M2.13 must ADD to it (a third view branch + the table's sort state). The
// ticket's near-cap rule requires the split to be the sub-phase's FIRST task,
// before any new lines land. The seam is the scroll container: everything the
// page composes ABOVE it (rail, toolbar) is chrome the user acts on, everything
// inside it is a rendering of the current result set. FleetPage keeps the hooks,
// the handlers and the shell; this file keeps the branch.
//
// THE ORDER OF THE BRANCH IS LOAD-BEARING AND IS PRESERVED VERBATIM (review F1
// / F4, M2.2):
//   · the "status filters ignored" note renders on its own terms;
//   · the LiveFleetStrip renders ABOVE the loading/empty chain, so toggling a
//     chip that matches nothing cannot unmount the strip holding it;
//   · only then loading -> empty -> view.
// Moving the strip into the chain is the exact bug F1 fixed. Do not reorder.
//
// THE STRIP STAYS V1-ONLY (M2.2 / M2.3 as-built): Fleet V2 carries the same
// information per section in its group health headers, and M2.13's table does
// the same through the SAME headers (FleetGroupHeader). The status FILTERS stay
// reachable in every view from the FilterRail's "Stav" section — one
// `fleetStatusFilters` state — so switching view never loses a filter.
//
// ============================================================
//  M2.16b — WHAT THE BRANCH DOES BELOW 1280
// ============================================================
// Three changes, all gated on the module's own breakpoint queries
// (useV2MediaQuery.ts) and none of them reachable at >= 1280:
//
// 1. THE LIVE FLEET STRIP IS DESKTOP-ONLY NOW. Its cells are `flex: 1 1 120px`
//    at `minHeight: 62` (LiveFleetStrip.tsx:92-100), so nine of them plus the
//    header block wrap to four rows on a 390px phone — roughly 250px of counter
//    above the first machine. Boards 05 and 06 draw no such block. Nothing is
//    LOST: the strip is a status FILTER, and below 1280 that filter is reachable
//    from the sheet's STAV section (M2.16a) and, on V2, from the counter chips
//    below — so this is the M2.16a "re-home, never remove" rule, not a deletion.
//
// 2. V2's STATUS COUNTER CHIPS (board 09) render HERE, beside the strip and
//    ABOVE the loading/empty chain, for the exact reason review F1 put the strip
//    there: a filter control may not be unmounted by the result of its own
//    filter. Toggle "Alarm" when nothing is in alarm and `visibleCount` goes to
//    0 — the empty-state Notice replaces the BODY, and the chip row that was
//    just tapped must survive it.
//
// 3. V2 FALLS BACK TO THE V1 GRID AT TABLET. mobile/README.md §V2: "phone only.
//    Tablet uses V1 grid — a middle density is pointless above 390." The
//    fallback is HERE rather than in the slice so the persisted `fleetView` is
//    never rewritten behind the user's back: rotate a tablet past 1280 and V2 is
//    still what they chose. The sheet's ZOBRAZENIE section hides the V2 chip at
//    tablet and lights V1 instead (fleetSheetSections.tsx), so the toggle can
//    never leave a tablet user pointing at a view nothing renders.
//
// THE BODY'S HORIZONTAL PADDING TRACKS THE CHROME'S (M2.16b, step 1a). This
// scroll area was `0 24px 24px` at EVERY width while the mobile chrome above it
// (FleetMobileHeader / FilterBarB / ActiveFilterChips, all M2.16a) pads to 14 —
// the design package's phone/tablet page margin (mobile/machinery-mobile.jsx
// MbFleetShell :219, `padding: "0 14px 14px"`). Below 1280 the search field
// therefore started 10px inside the cards beneath it. The 24 is KEPT verbatim at
// >= 1280: the desktop body sits beside a rail and is not the same measurement.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { V2_BELOW_DESKTOP, V2_PHONE, useV2MediaQuery } from "../useV2MediaQuery";
import { MachineryV2FleetView } from "../machineryV2UiSlice";
import { FilterNote, Notice } from "./FleetNotices";
import { FleetScope } from "./FleetToolbar";
import FleetStatusChips from "./FleetStatusChips";
import FleetV1Page from "./FleetV1Page";
import FleetV2Page, { FleetV2PageProps } from "./FleetV2Page";
import LiveFleetStrip from "./LiveFleetStrip";
import { MachineryV2CardState } from "./cardStates";
import { FleetPageData } from "./fleetPageData";
import FleetTableView from "./table/FleetTableView";
import { FleetTableSort, FleetTableSortColumn } from "./table/fleetTableSort";

/** Everything a body needs EXCEPT the sections it renders — one object, built
 *  once by the page, handed to whichever body is mounted. */
export type FleetBodyProps = Omit<FleetV2PageProps, "sections">;

export interface FleetContentProps {
  data: FleetPageData;
  scope: FleetScope;
  view: MachineryV2FleetView;
  statusFilters: MachineryV2CardState[];
  onToggleStatus: (state: MachineryV2CardState) => void;
  /** V3 only — held by the page so a V1/V3 round trip keeps the ordering. */
  sort: FleetTableSort;
  onSortChange: (column: FleetTableSortColumn) => void;
  body: FleetBodyProps;
}

const FleetContent: React.FC<FleetContentProps> = ({
  data, scope, view, statusFilters, onToggleStatus, sort, onSortChange, body,
}) => {
  const { translate } = useTranslation("myMachinery");
  // F-A: both tiers come from the module's own queries — the same strings the
  // v2 stylesheets fence on. See useV2MediaQuery.ts.
  const isPhone = useV2MediaQuery(V2_PHONE);
  const isDesktop = !useV2MediaQuery(V2_BELOW_DESKTOP);
  // README §V2 — a middle density is pointless above 390, so V2 IS the V1 grid
  // between 768 and 1279. Desktop V2 is untouched.
  const body2AsGrid = view === "V2" && !isPhone && !isDesktop;

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        padding: isDesktop ? "0 24px 24px" : "0 14px 14px",
      }}
    >
      {/* REVIEW F4 — status filters are telemetry-derived, and the
          decommissioned scope has no telemetry. Rather than silently
          matching nothing, say so. The filter is never cleared behind the
          user's back; switching back to Aktívne restores it. */}
      {data.statusFiltersIgnored && (
        <FilterNote text={translate("myMachinery:v2.fleet.statusFiltersIgnored")} />
      )}

      {/* REVIEW F1 — RENDERED ON ITS OWN TERMS, ABOVE THE BRANCH. Inside
          the loading/empty chain, toggling a status chip that matched
          nothing unmounted the very strip holding it, and any thunk that
          flips `loading` (createMachine / restoreMachine included) flashed
          it away mid-action. A filter control must outlive the result of
          its own filter. */}
      {scope === "active" && view === "V1" && isDesktop && (
        <LiveFleetStrip
          total={data.flat.length}
          connected={data.connectedCount}
          counts={data.stateCounts}
          selected={statusFilters}
          onToggle={onToggleStatus}
        />
      )}

      {/* Board 09's counter row — ADDITIVE to pattern B's row 1, never a
          replacement for it (the manager ruling; see FleetStatusChips.tsx).
          Same placement discipline as the strip above: on its own terms, above
          the loading/empty chain. */}
      {scope === "active" && view === "V2" && isPhone && (
        <FleetStatusChips
          counts={data.stateCounts}
          selected={statusFilters}
          onToggle={onToggleStatus}
        />
      )}

      {data.isLoading ? (
        <Notice text={translate("myMachinery:v2.fleet.loading")} dashed={false} />
      ) : data.visibleCount === 0 ? (
        <Notice
          text={translate(
            scope === "decommissioned"
              ? "myMachinery:noDecommissionedMachines"
              : "myMachinery:noMachinesFound"
          )}
        />
      ) : view === "V3" ? (
        <FleetTableView
          sections={data.sections}
          sort={sort}
          onSortChange={onSortChange}
          {...body}
        />
      ) : view === "V2" && !body2AsGrid ? (
        <FleetV2Page sections={data.sections} {...body} />
      ) : (
        <FleetV1Page sections={data.sections} {...body} />
      )}
    </div>
  );
};

export default FleetContent;
